import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
} from "@mui/material";
import colors from "../../Constants/colors";
import { HexColorPicker } from "react-colorful";
import ColorPickerField from "./ColorPickerField";

interface CustomizeWorkspaceModalProps {
  open: boolean;
  onClose: () => void;
  color: {
    primary: string;
    secondary: string;
    accent: string;
  };
  setColors: (updatedColors: {
    primary: string;
    secondary: string;
    accent: string;
  }) => void;
}

const CustomizeWorkspaceModal: React.FC<CustomizeWorkspaceModalProps> = ({
  open,
  onClose,
  color,
  setColors,
}) => {
  const { t } = useTranslation("workspaces");
  const [primary, setPrimary] = useState(color.primary || colors.primary_dark);
  const [secondary, setSecondary] = useState(color.secondary || colors.white);
  const [accent, setAccent] = useState(color.accent || colors.accent);
  const [editingColor, setEditingColor] = useState<string | null>("primary");

  const handleColorChange = (newColor: string) => {
    if (editingColor === "primary") setPrimary(newColor);
    if (editingColor === "secondary") setSecondary(newColor);
    if (editingColor === "accent") setAccent(newColor);

    setColors({
      primary: editingColor === "primary" ? newColor : primary,
      secondary: editingColor === "secondary" ? newColor : secondary,
      accent: editingColor === "accent" ? newColor : accent,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography
          color={colors.primary_dark}
          sx={{
            fontSize: "1.5rem",
            fontWeight: "bold",
            borderBottom: `2px solid ${colors.accent}`,
            paddingBottom: "10px",
          }}
        >
          {t("customizeWorkspace")}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Grid
          container
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Grid
            item
            sx={{
              display: "flex",
              flexDirection: "column",
              marginBottom: "1rem",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <HexColorPicker
              color={
                editingColor === "primary"
                  ? primary
                  : editingColor === "secondary"
                  ? secondary
                  : accent
              }
              onChange={handleColorChange}
            />
          </Grid>
          <ColorPickerField
            label={t("primary")}
            value={primary}
            onFocus={() => setEditingColor("primary")}
            onChange={(newColor) => handleColorChange(newColor)}
          />
          <ColorPickerField
            label={t("secondary")}
            value={secondary}
            onFocus={() => setEditingColor("secondary")}
            onChange={(newColor) => handleColorChange(newColor)}
          />
          <ColorPickerField
            label={t("accent")}
            value={accent}
            onFocus={() => setEditingColor("accent")}
            onChange={(newColor) => handleColorChange(newColor)}
          />
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          color="primary"
          variant="contained"
          sx={{
            backgroundColor: colors.accent,
            color: colors.white,
            "&:hover": {
              backgroundColor: colors.primary_dark,
              color: colors.accent,
            },
          }}
        >
          {t("close")}
        </Button>
        <Button
          onClick={() => {
            setColors({ primary, secondary, accent });
            onClose();
          }}
          color="primary"
          variant="contained"
          sx={{
            backgroundColor: colors.accent,
            color: colors.white,
            "&:hover": {
              backgroundColor: colors.primary_dark,
              color: colors.accent,
            },
          }}
        >
          {t("apply")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomizeWorkspaceModal;
